import { Agent, PartialAgent } from "~/services/agents";
import { apiMutate } from "~/utils/apiClient";

// Project CRUD and attachments live on the conversations service. Only the
// "start a conversation" mutation still flows through the analysis-agent
// because it has to derive `app_id` from the bound agent — that lookup
// belongs alongside the agents the analysis-agent already owns.
const { VITE_ANALYSIS_AGENT_API, VITE_CONVERSATIONS_API } = import.meta.env;
export const PROJECTS_API_PREFIX = `${VITE_CONVERSATIONS_API}/projects`;
const ANALYSIS_AGENT_PROJECTS_PREFIX = `${VITE_ANALYSIS_AGENT_API}/projects`;

export const projectListPath = (params?: { limit?: number; offset?: number }) => {
  const query = new URLSearchParams();
  if (params?.limit !== undefined) {
    query.set("limit", String(params.limit));
  }
  if (params?.offset !== undefined) {
    query.set("offset", String(params.offset));
  }
  const qs = query.toString();
  return qs ? `${PROJECTS_API_PREFIX}?${qs}` : PROJECTS_API_PREFIX;
};

export const projectPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}`;

export const projectAgentsPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/agents`;

export const projectDatasetsPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/datasets`;

export const projectConversationsPath = (id: string) =>
  `${PROJECTS_API_PREFIX}/${id}/conversations`;

export const projectLeavePath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/leave`;

export const projectSharesPath = (id: string) => `${PROJECTS_API_PREFIX}/${id}/shares`;

export const projectSharesBatchPath = () => `${PROJECTS_API_PREFIX}/shares`;

// `POST /projects/{id}/conversations` still hits the analysis-agent so it can
// look up the agent and stamp `app_id` onto the new conversation row.
const projectConversationsCreatePath = (id: string) =>
  `${ANALYSIS_AGENT_PROJECTS_PREFIX}/${id}/conversations`;

export interface PartialProject {
  id: string;
  short_id: string;
  owner_user_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  // The caller's own access to this project, resolved per-request against
  // OpenFGA. Drives gating of owner-only controls in the UI.
  access_level: ProjectAccessLevel;
}

export type Project = PartialProject;

export interface ProjectListItem extends PartialProject {
  agents: PartialAgent[];
  dataset_count: number;
  // Greater of the project's creation time and the caller's newest
  // conversation update in it.
  last_activity_at: string;
}

export interface ProjectListMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

export interface ProjectList {
  items: ProjectListItem[];
  metadata: ProjectListMetadata;
}

export interface CreateProjectInput {
  name: string;
  description?: string | null;
}

export interface UpdateProjectInput {
  name?: string;
  description?: string | null;
}

export async function createProject(input: CreateProjectInput): Promise<Project> {
  return apiMutate<Project>(PROJECTS_API_PREFIX, "POST", input);
}

export async function updateProject(id: string, input: UpdateProjectInput): Promise<Project> {
  return apiMutate<Project>(projectPath(id), "PATCH", input);
}

export async function deleteProject(id: string): Promise<Project> {
  return apiMutate<Project>(projectPath(id), "DELETE");
}

// Remove the caller's own access. The backend returns 204; a 409 means the
// caller is the project's only owner (or access is group-inherited).
export async function leaveProject(id: string): Promise<void> {
  await apiMutate<null>(projectLeavePath(id), "POST", undefined, { expects204: true });
}

export async function setProjectAgents(id: string, agent_ids: string[]): Promise<Agent[]> {
  return apiMutate<Agent[]>(projectAgentsPath(id), "PUT", { agent_ids });
}

export async function setProjectDatasets(id: string, dataset_ids: string[]): Promise<string[]> {
  return apiMutate<string[]>(projectDatasetsPath(id), "PUT", { dataset_ids });
}

export interface ProjectConversation {
  id: string;
  user_id: string;
  agent_id: string | null;
  agent: PartialAgent | null;
  project_id: string;
  project_uuid: string;
  display_name: string;
  status: string;
  status_updated_at: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectConversationList {
  items: ProjectConversation[];
  limit: number | null;
  offset: number;
  total: number;
}

export interface CreateProjectConversationInput {
  agent_id: string;
  user_timezone?: string;
}

export async function createProjectConversation(
  projectId: string,
  input: CreateProjectConversationInput
): Promise<ProjectConversation> {
  return apiMutate<ProjectConversation>(projectConversationsCreatePath(projectId), "POST", input);
}

// Project sharing. Backend maps USER -> can_use and OWNER -> can_manage in
// OpenFGA; principals are users (by email) or user-tags (groups, by name).
export const ProjectAccessLevel = {
  USER: "user",
  OWNER: "owner",
} as const;
export type ProjectAccessLevel = (typeof ProjectAccessLevel)[keyof typeof ProjectAccessLevel];

// A user who inherits access through a shared group (user-tag). Populated only
// on the single-project collaborators endpoint, not the batch list.
export interface ProjectCollaboratorMember {
  email: string;
  given_name: string | null;
  family_name: string | null;
  image_url: string | null;
}

export interface ProjectCollaborator {
  /** Bare prefixed principal: `user:{email}` or `user-tag:{tag}`. */
  principal: string;
  level: ProjectAccessLevel;
  // Profile fields, populated for user principals; null for groups.
  given_name: string | null;
  family_name: string | null;
  image_url: string | null;
  // Group (user-tag) fields, populated for user-tag principals; absent/empty for users.
  display_name?: string | null;
  is_internal?: boolean;
  members?: ProjectCollaboratorMember[];
}

export interface ProjectCollaboratorList {
  items: ProjectCollaborator[];
}

/** Share a project with a principal at the given level. Idempotent: re-sharing
 * an existing `(principal, level)` succeeds. */
export async function shareProject(
  id: string,
  principal: string,
  level: ProjectAccessLevel
): Promise<ProjectCollaborator> {
  return apiMutate<ProjectCollaborator>(projectSharesPath(id), "POST", { principal, level });
}

/** Unshare a project from a principal. Idempotent: unsharing an absent share
 * still succeeds. */
export async function unshareProject(
  id: string,
  principal: string,
  level: ProjectAccessLevel
): Promise<void> {
  await apiMutate(projectSharesPath(id), "DELETE", { principal, level }, { expects204: true });
}

// Collaborators for a page of projects, keyed by project id. Fetched in a second
// phase after the list renders so collaborator avatars don't block (or break)
// the list. Projects the caller can't use are omitted from the mapping.
export interface ProjectCollaboratorsBatch {
  collaborators_by_project: Record<string, ProjectCollaborator[]>;
}

export async function fetchProjectCollaboratorsBatch(
  projectIds: string[]
): Promise<ProjectCollaboratorsBatch> {
  return apiMutate<ProjectCollaboratorsBatch>(projectSharesBatchPath(), "POST", {
    project_ids: projectIds,
  });
}
